import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Add } from 'iconsax-react';
import FilterPanel from './FilterPanel';
import VistaOra from './VistaOra';
import VistaGiorno from './VistaGiorno';
import VistaSettimana from './VistaSettimana';
import VistaMese from './VistaMese';
import CreaEventoModal from './CreaEventoModal';
import DettaglioEventoModal from './DettaglioEventoModal';
import MyApiClient from '../../../../../api-client/MyApiClient';

const api = new MyApiClient();

function getToken() {
    try { const s = sessionStorage.getItem('userDetails'); return s ? JSON.parse(s).idToken : null; }
    catch { return null; }
}

const VIEWS = [
    { key: 'ora',       label: 'Ora' },
    { key: 'giorno',    label: 'Giorno' },
    { key: 'settimana', label: 'Settimana' },
    { key: 'mese',      label: 'Mese' },
];

export default function StrutturaCalendarioEventi() {
    const token = useMemo(getToken, []);

    const [view,              setView]              = useState('settimana');
    const [modale,            setModale]            = useState(false);
    const [eventoSelezionato, setEventoSelezionato] = useState(null);

    // API state
    const [eventi,       setEventi]       = useState([]);
    const [risorse,      setRisorse]      = useState({ impianti: [], maestri: [], corsi: [] });
    const [loading,      setLoading]      = useState(false);
    const [currentRange, setCurrentRange] = useState(null); // { da, a } — ISO strings

    // Filters
    const [filtroStato, setFiltroStato] = useState('');   // '' | 'pagato' | 'non_pagato'
    const [checkedIds,  setCheckedIds]  = useState({});   // { id: bool }
    const [mostraGenerici, setMostraGenerici] = useState(true);

    // Shared date for Giorno / Ora
    const [selectedDate, setSelectedDate] = useState(new Date());
    // Shared starting hour for Ora (set when arriving from Giorno)
    const [oraIniziale,  setOraIniziale]  = useState(null);

    // Fetch resources when range changes
    useEffect(() => {
        if (!token || !currentRange) return;
        const da = currentRange.da.substring(0, 10);
        const a  = currentRange.a.substring(0, 10);
        api.getCalendarioRisorse(token, da, a).then(res => {
            if (!res?.error) {
                setRisorse(res);
                setCheckedIds(prev => {
                    const next = { ...prev };
                    [...(res.impianti || []), ...(res.maestri || []), ...(res.corsi || [])]
                        .forEach(r => { if (!(r.id in next)) next[r.id] = true; });
                    return next;
                });
            }
        });
    }, [token, currentRange]);

    const fetchEventi = useCallback(async (range) => {
        if (!token || !range) return;
        setLoading(true);

        const filters = {};
        if (filtroStato) filters.statoPagemento = filtroStato;

        const impiantiOn = (risorse.impianti || []).filter(r => checkedIds[r.id] !== false).map(r => r.id);
        const maestriOn  = (risorse.maestri  || []).filter(r => checkedIds[r.id] !== false).map(r => r.id);
        const corsiOn    = (risorse.corsi    || []).filter(r => checkedIds[r.id] !== false).map(r => r.id);

        // Send resource filter only when some are excluded
        if (impiantiOn.length < (risorse.impianti || []).length && impiantiOn.length > 0)
            filters.impianti = impiantiOn.join(',');
        if (maestriOn.length  < (risorse.maestri  || []).length && maestriOn.length > 0)
            filters.maestri  = maestriOn.join(',');
        if (corsiOn.length    < (risorse.corsi    || []).length && corsiOn.length > 0)
            filters.corsi    = corsiOn.join(',');

        // Exclude entire type if all its resources are unchecked
        const excl = [];
        if ((risorse.impianti || []).length > 0 && impiantiOn.length === 0) excl.push('impianto');
        if ((risorse.maestri  || []).length > 0 && maestriOn.length  === 0) excl.push('lezione');
        if ((risorse.corsi    || []).length > 0 && corsiOn.length    === 0) excl.push('corso');
        if (!mostraGenerici) excl.push('generico');
        if (excl.length > 0)
            filters.tipi = ['impianto','lezione','corso','generico'].filter(t => !excl.includes(t)).join(',');

        const res = await api.getCalendarioEventi(token, range.da, range.a, filters);
        setLoading(false);
        if (res && !res.error) setEventi(res.eventi || []);
    }, [token, filtroStato, risorse, checkedIds, mostraGenerici]);

    useEffect(() => { fetchEventi(currentRange); }, [fetchEventi, currentRange]);

    const handleRangeChange = useCallback((da, a) => setCurrentRange({ da, a }), []);

    const handleToggleRisorsa = useCallback(
        (id) => setCheckedIds(prev => ({ ...prev, [id]: prev[id] === false })),
        []
    );

    const handleToggleSection = useCallback(
        (ids, checked) => setCheckedIds(prev => {
            const next = { ...prev };
            for (const id of ids) next[id] = checked;
            return next;
        }),
        []
    );

    const handleFiltroStato = useCallback((label) => {
        const map = { 'Tutto': '', 'Pagato': 'pagato', 'Non pagato': 'non_pagato' };
        setFiltroStato(map[label] ?? '');
    }, []);

    const onCreated = useCallback(() => fetchEventi(currentRange), [fetchEventi, currentRange]);
    const onRefresh = useCallback(() => fetchEventi(currentRange), [fetchEventi, currentRange]);

    return (
        <>
        <div
            className="card"
            style={{
                margin: 0, borderRadius: '1.25rem', overflow: 'hidden',
                border: '1px solid #e8e8e8',
                boxShadow: '0 0 2.5rem 0 rgba(82,63,105,0.08)',
                display: 'flex', flexDirection: 'column',
                height: 'calc(100vh - 110px)', minHeight: 600,
            }}
        >
            {/* ── TOP BAR ─────────────────────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0 24px', height: 56,
                borderBottom: '1px solid #eee', background: '#fff', flexShrink: 0,
            }}>
                <div style={{ display: 'flex', gap: 4, height: '100%', alignItems: 'center' }}>
                    {VIEWS.map(v => {
                        const active = view === v.key;
                        return (
                            <button key={v.key} onClick={() => setView(v.key)} style={{
                                padding: '6px 16px', fontSize: 13,
                                fontWeight: active ? 700 : 500, border: 'none', borderRadius: 6,
                                background: active ? '#FF5E4B' : 'transparent',
                                color: active ? '#fff' : '#888', cursor: 'pointer', transition: 'all 0.15s',
                            }}>
                                {v.label}
                            </button>
                        );
                    })}
                    {loading && (
                        <span style={{ fontSize: 12, color: '#bbb', marginLeft: 8 }}>●</span>
                    )}
                </div>

                <button onClick={() => setModale(true)} style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    background: '#FF5E4B', color: '#fff', border: 'none', borderRadius: 6,
                    padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(255,94,75,0.28)', whiteSpace: 'nowrap',
                }}>
                    <Add size={16} color="#fff" />
                    Crea evento
                </button>
            </div>

            {/* ── BODY ────────────────────────────────────────────── */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid #eee', overflowY: 'auto', background: '#fff' }}>
                    <FilterPanel
                        view={view}
                        risorse={risorse}
                        checkedIds={checkedIds}
                        onToggleRisorsa={handleToggleRisorsa}
                        onToggleSection={handleToggleSection}
                        filtroStatoLabel={filtroStato === 'pagato' ? 'Pagato' : filtroStato === 'non_pagato' ? 'Non pagato' : 'Tutto'}
                        onFiltroStato={handleFiltroStato}
                        selectedDate={selectedDate}
                        onDateSelect={setSelectedDate}
                        mostraGenerici={mostraGenerici}
                        onToggleGenerici={() => setMostraGenerici(v => !v)}
                    />
                </div>
                <div style={{ flex: 1, overflow: 'hidden', background: '#fff' }}>
                    {view === 'ora'       && <VistaOra       eventi={eventi} selectedDate={selectedDate} oraIniziale={oraIniziale} onRangeChange={handleRangeChange} onEventClick={setEventoSelezionato} />}
                    {view === 'giorno'    && <VistaGiorno    eventi={eventi} selectedDate={selectedDate} onDateChange={setSelectedDate} onRangeChange={handleRangeChange} onEventClick={setEventoSelezionato} onOraClick={(ora) => { setOraIniziale(ora); setView('ora'); }} />}
                    {view === 'settimana' && <VistaSettimana eventi={eventi} onRangeChange={handleRangeChange} onEventClick={setEventoSelezionato} onDayClick={(d) => { setSelectedDate(d); setView('giorno'); }} />}
                    {view === 'mese'      && <VistaMese      eventi={eventi} onRangeChange={handleRangeChange} onEventClick={setEventoSelezionato} onDayClick={(d) => { setSelectedDate(d); setView('giorno'); }} />}
                </div>
            </div>
        </div>

        {modale && <CreaEventoModal onClose={() => setModale(false)} token={token} onCreated={onCreated} />}
        {eventoSelezionato && (
            <DettaglioEventoModal
                event={eventoSelezionato}
                onClose={() => setEventoSelezionato(null)}
                token={token}
                onRefresh={onRefresh}
            />
        )}
        </>
    );
}
